export class InMemoryStorage {
  readonly name = 'InMemoryStorage'

  /** Simple object to store data in memory (no persistence beyond this tab). */
  private data: Record<string, string> = {}

  /**
   * For each key, store a Set of listeners so we can notify them when that key changes.
   */
  private listeners = new Map<string, Set<() => void>>()

  /**
   * BroadcastChannel for cross-tab communication. If unsupported, stays undefined.
   */
  private channel?: BroadcastChannel

  private channelName = 'KeyValueStorage:InMemoryStorage'

  constructor(opts?: { syncTabs?: boolean }) {
    const syncTabs = opts?.syncTabs ?? true

    if (typeof BroadcastChannel !== 'undefined' && syncTabs) {
      this.channel = new BroadcastChannel(this.channelName)
      // Listen for messages from other tabs
      this.channel.addEventListener('message', this.onMessage)
    }
  }

  getItem(key: string): string | null {
    return this.data[key] ?? null
  }

  setItem(key: string, value: string): void {
    this.data[key] = value

    this.notifyLocalChange(key)
    this.broadcastMessage({ type: 'set', key, value })
  }

  removeItem(key: string): void {
    // 1) Remove locally
    delete this.data[key]

    this.notifyLocalChange(key)
    this.broadcastMessage({ type: 'remove', key })
  }

  addChangeListener(key: string, listener: () => void): void {
    let keyListeners = this.listeners.get(key)
    if (!keyListeners) {
      keyListeners = new Set()
      this.listeners.set(key, keyListeners)
    }
    keyListeners.add(listener)
  }

  removeChangeListener(key: string, listener: () => void): void {
    const keyListeners = this.listeners.get(key)
    if (!keyListeners) return

    keyListeners.delete(listener)
    if (keyListeners.size === 0) {
      this.listeners.delete(key)
    }
  }

  /**
   * Handle incoming messages from the BroadcastChannel.
   * Notice we do NOT call setItem/removeItem here, so we won't re-postMessage and loop.
   */
  private onMessage = (event: MessageEvent) => {
    const message = event.data as BroadcastMessage

    switch (message.type) {
      case 'set':
        this.data[message.key] = message.value
        this.notifyLocalChange(message.key)
        break
      case 'remove':
        delete this.data[message.key]
        this.notifyLocalChange(message.key)
        break
    }
  }

  private broadcastMessage(message: BroadcastMessage) {
    this.channel?.postMessage(message)
  }

  /**
   * Notify all local listeners of a change to the given key.
   */
  private notifyLocalChange(key: string) {
    const keyListeners = this.listeners.get(key)
    if (!keyListeners) return

    for (const listener of keyListeners) {
      listener()
    }
  }
}

type BroadcastMessage =
  | {
      type: 'set'
      key: string
      value: string
    }
  | {
      type: 'remove'
      key: string
    }
